<script lang="ts" setup>
import { safeParse as vSafeParse } from 'valibot';
import srcValuationEN from '~/assets/images/real-estate-agent/valuation_en.jpg';
import srcValuationFR from '~/assets/images/real-estate-agent/valuation_fr.jpg';
import { ErrorSchema, type IadAppRoute } from '~/application';

definePageMeta({
  pageTemplate: 'estimation_form',
  pageType: 'collaborator',
});

const route = useRoute();
const i18nHead = useLocaleHead({ key: 'id' });
const { t, locale } = useI18n();

// “error” has to be renamed, it was crashing during tests
// https://tanstack.com/query/latest/docs/framework/vue/examples/nuxt3
const { data: agent, suspense, error: agentError } = useFetchAgent(route.params.userName);
await suspense();

const agentUserName = computed(() => {
  const userName = route.params.userName;
  return typeof userName === 'string' ? userName : undefined;
});

const localePath = useLocalePath();
const errorResult = vSafeParse(ErrorSchema, agentError.value);
const searchAgentRoute: IadAppRoute<'find-real-estate-agent'> = {
  name: `find-real-estate-agent`,
  params: {},
};
if (errorResult.success && errorResult.output.statusMessage === `DB_AGENT_USERNAME_NOT_FOUND`) {
  const path = localePath(searchAgentRoute, locale.value);
  if (path) await navigateTo({ path }, { replace: true, redirectCode: 301 });
}
else if (agent.value && agent.value.isDeactivated) {
  const redirectPath = (agent.value.breadcrumbs.at(-2)?.to as IadAppRoute<'find-real-estate-agent-agentSector'>) ?? searchAgentRoute;
  const path = localePath(redirectPath, locale.value);
  if (path) await navigateTo({ path }, { replace: true, redirectCode: 301 });
}

const seoMeta = computed(() => {
  const descriptionKey = agent.value?.displayGender === 'female'
    ? 'agent.valuation.meta.description_female'
    : 'agent.valuation.meta.description_male';
  const description = t(descriptionKey, {
    fullName: agent.value?.fullName,
    locality: agent.value?.location.place,
    postcode: agent.value?.location.postcode,
  });
  const title = t('agent.valuation.meta.title', { fullName: agent.value?.fullName });
  return { description, title };
});

const openGraphImageSrc = computed(() => locale.value === 'fr' ? srcValuationFR : srcValuationEN);

useHead({
  title: seoMeta.value.title,
  meta: [
    ...(i18nHead.value?.meta ?? []),
    { name: 'description', content: seoMeta.value.description },
    { name: 'robots', content: 'noindex' },
    { property: `og:image`, content: openGraphImageSrc },
  ],
  // This is a workaround to remove the tags generated by the i18nHead plugin
  // https://unhead.unjs.io/docs/typescript/head/guides/core-concepts/handling-duplicates#removing-a-tag-from-a-parent-component
  link: [
    { id: 'i18n-alt-fr' },
    { id: 'i18n-alt-en' },
    { id: 'i18n-xd' },
    { rel: 'canonical' },
  ],
});

const displayError = computed(() => {
  return agentError.value instanceof Error && 'statusCode' in agentError.value && agentError.value.statusCode === 404;
});

const displayContactForm = ref(false);
const soldSalesProperties = computed(() => agent.value?.latestTransactions.sold.length ?? 0 > 0);
</script>

<template>
  <div class="content">
    <IadPlaceholderNotFound v-if="displayError" />
    <main v-if="agent">
      <AgentSectionValuationHeader :agent />
      <ValuationSearchForm
        :title="$t(`agent.estimate.stepper.title`)"
        class="mt-[-160px]"
        :agent-user-name
      />
      <div class="flex flex-col gap-2xl">
        <AgentValuationStep />
        <AgentValuationSoldSaleProperties
          v-if="soldSalesProperties"
          :agent
        />
        <AgentSectionReviews
          v-if="agent.reviewsCount"
          variant="full"
          :agent
        />
        <ValuationObjectives />
        <AgentValuationReinsurance />
      </div>

      <IadWrapper
        class="py-2xl"
        tag="div"
      >
        <ol class="whitespace-pre-line text-neutral-40">
          <li v-text="$t('agent.valuation.reinsurance.legal_notice.first')" />
          <li v-text="$t('agent.valuation.reinsurance.legal_notice.second')" />
        </ol>
      </IadWrapper>
    </main>

    <ClientOnly>
      <AgentNavigationFixed
        v-if="agent"
        class="lg:hidden"
        :agent
        @contact="displayContactForm = true"
      />
      <AgentDialogContact
        v-model="displayContactForm"
        :agent
      />
    </ClientOnly>
  </div>
</template>
